import { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Download, Lock, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_HREF = "/assets/armada-manifesto-vol-0.pdf";
const STORAGE_KEY = "nrn.armada.access";

function triggerDownload() {
  const a = document.createElement("a");
  a.href = PDF_HREF;
  a.download = "Armada-Manifesto-Vol-0.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function WaitlistGate({ open, onOpenChange }) {
  const [email, setEmail] = useState("");
  const [callsign, setCallsign] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setSuccess(false);
        setLoading(false);
      }, 200);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      toast.error("Transmission rejected", {
        description: "A valid email is required for sovereign access.",
      });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email: value,
        callsign: callsign.trim() || null,
        source: "manifesto-vol-0",
      });
      localStorage.setItem(STORAGE_KEY, value);
      setSuccess(true);
      toast.success("Access granted · Sovereign Operator", {
        description: "Decrypting the manifesto…",
      });
      setTimeout(() => {
        triggerDownload();
        setTimeout(() => onOpenChange(false), 900);
      }, 500);
    } catch (err) {
      toast.error("Vault unreachable", {
        description: err?.response?.data?.detail || "Try again momentarily.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-obsidian-surface border border-gold/30 sm:max-w-md p-0 overflow-hidden"
        data-testid="waitlist-dialog"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
        <div className="p-7">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-violet animate-pulse-violet" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-violet-soft">
              Sovereign Access · Required
            </span>
          </div>

          <DialogHeader className="text-left space-y-2">
            <DialogTitle className="font-heading text-2xl text-white">
              Identify Yourself
            </DialogTitle>
            <DialogDescription className="text-white/55 leading-relaxed">
              The Armada Manifesto is sealed. Sovereign operators only. Leave a
              callsign and the vault releases <span className="text-gold">Vol 0</span>.
            </DialogDescription>
          </DialogHeader>

          {!success ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/45 block mb-1.5">
                  Secure Channel · Email
                </label>
                <Input
                  data-testid="waitlist-email-input"
                  type="email"
                  required
                  placeholder="operator@domain.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-black/60 border-white/10 text-white font-mono placeholder:text-white/25 focus-visible:border-gold/50 focus-visible:ring-gold/20 h-11"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/45 block mb-1.5">
                  Callsign · Optional
                </label>
                <Input
                  data-testid="waitlist-callsign-input"
                  type="text"
                  placeholder="The Helmsman"
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value)}
                  className="bg-black/60 border-white/10 text-white font-mono placeholder:text-white/25 focus-visible:border-gold/50 focus-visible:ring-gold/20 h-11"
                />
              </div>

              <div className="flex items-center gap-2 text-white/40 font-mono text-[10px] tracking-[0.18em] uppercase pt-1">
                <Lock className="w-3 h-3 text-gold/70" />
                AES-256 · No spam · You can vanish anytime
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit-btn"
                className="w-full h-11 bg-gold hover:bg-gold-soft text-obsidian font-mono text-[11px] tracking-[0.22em] uppercase rounded-full shadow-[0_8px_28px_-8px_rgba(212,175,55,0.6)]"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Authenticating…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" /> Receive the Manifesto
                  </>
                )}
              </Button>
            </form>
          ) : (
            <div
              className="mt-6 rounded-md border border-gold/40 bg-gold/5 p-5 text-center"
              data-testid="waitlist-success"
            >
              <ShieldCheck className="w-7 h-7 text-gold mx-auto mb-3" />
              <div className="font-heading text-lg text-white">
                Welcome to the Armada
              </div>
              <p className="mt-1.5 text-sm text-white/60">
                Decryption sequence engaged. The vault is opening…
              </p>
              <div className="mt-4 font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80">
                · Manifesto Vol 0 · Inbound ·
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function hasArmadaAccess() {
  try {
    return Boolean(localStorage.getItem(STORAGE_KEY));
  } catch {
    return false;
  }
}

export { triggerDownload };
